import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { GrandSigil } from "./Sigil";
import { scrollToId, ease } from "@/lib/scroll";

const lines = [
  [{ t: "Pleasure is" }],
  [{ t: "a " }, { t: "living", i: true }],
  [{ t: "language." }],
];

const Line = ({ parts, idx, d }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.3, delay: d + 0.05 + idx * 0.14, ease }}
    >
      {parts.map((p, k) => (
        <span key={k} className={p.i ? "italic text-[#D9B47A]" : ""}>{p.t}</span>
      ))}
    </motion.span>
  </span>
);

export default function Hero({ delay: d = 2.3 }) {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const cx = useSpring(useMotionValue(-100), { stiffness: 300, damping: 30 });
  const cy = useSpring(useMotionValue(-100), { stiffness: 300, damping: 30 });
  const rx = useSpring(useTransform(my, [0, 1], [14, -14]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-18, 18]), { stiffness: 60, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const spot = useMotionValue("50% 40%");
  const tiltX = useTransform(scrollYProgress, [0, 1], [0, 62]);
  const sigilRX = useTransform([rx, tiltX], ([a, b]) => a + b);
  const sigilRZ = useTransform(scrollYProgress, [0, 1], [0, 48]);
  const sigilScale = useTransform(scrollYProgress, [0, 1], [1, 1.3]);
  const sigilZ = useTransform(scrollYProgress, [0, 1], [0, -260]);
  const frameRX = useTransform(rx, (v) => v * 0.45);
  const frameRY = useTransform(ry, (v) => v * 0.45);
  const frameScrollRX = useTransform(scrollYProgress, [0, 1], [0, -16]);
  const frameTilt = useTransform([frameRX, frameScrollRX], ([a, b]) => a + b);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
    cx.set(e.clientX - r.left);
    cy.set(e.clientY - r.top);
  };
  const onImgMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    spot.set(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };
  const spotBg = useTransform(spot, (s) => `radial-gradient(circle at ${s}, transparent 0%, rgba(10,7,6,0.15) 22%, rgba(10,7,6,0.88) 62%)`);

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="hero-zone relative min-h-[100svh] overflow-hidden pt-28 pb-16 sm:pt-32"
    >
      <motion.div
        className="hero-cursor pointer-events-none absolute z-30 w-10 h-10 -ml-5 -mt-5 rounded-full border border-[#D9B47A] mix-blend-difference"
        style={{ x: cx, y: cy }}
      />
      <div className="absolute inset-0 [perspective:1200px] flex items-center justify-center lg:justify-end lg:pr-[14%] pointer-events-none">
        <motion.div data-testid="hero-3d-sigil" style={{ rotateX: sigilRX, rotateY: ry, rotateZ: sigilRZ, scale: sigilScale, z: sigilZ }} className="w-[140vw] sm:w-[90vw] lg:w-[62vw] max-w-[980px] opacity-40 will-change-transform">
          <GrandSigil className="w-full h-full" />
        </motion.div>
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-12 items-end min-h-[calc(100svh-10rem)]">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.max(0, d - 0.1), duration: 1 }}
            className="font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A] mb-8"
            data-testid="hero-kicker"
          >
            Goddess Ariel · Priestess of Pleasure
          </motion.p>
          <h1 data-testid="hero-title" className="font-display text-[clamp(3.2rem,10.5vw,10rem)] leading-[0.9] tracking-[-0.02em]">
            {lines.map((l, i) => <Line key={i} parts={l} idx={i} d={d} />)}
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: d + 0.6, duration: 1.1, ease }}
            className="mt-10 max-w-xl"
          >
            <p data-testid="hero-subtitle" className="text-base sm:text-lg text-[#CFC2B6] leading-relaxed">
              Thirteen Gates. One body. A path to orgasmic enlightenment, mapped with anatomical precision and
              offered with reverence. Beloved, the temple was never somewhere else.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-cta-gates"
                onClick={() => scrollToId("gates")}
                className="group inline-flex items-center gap-3 rounded-full bg-[#D9B47A] text-[#0A0706] px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors duration-300"
              >
                Walk the 13 Gates
                <ArrowDownRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-[-45deg]" />
              </button>
              <button
                data-testid="hero-cta-guide"
                onClick={() => scrollToId("enter")}
                className="rounded-full border border-[#F2E8DF]/30 px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:border-[#D9B47A] hover:text-[#D9B47A] transition-colors duration-300"
              >
                Free Starter Guide
              </button>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ delay: d + 0.1, duration: 1.6, ease }}
          className="lg:col-span-5 relative [perspective:1400px]"
        >
          <motion.div
            onMouseMove={onImgMove}
            data-testid="hero-image-frame"
            style={{ rotateX: frameTilt, rotateY: frameRY, transformStyle: "preserve-3d" }}
            className="arch relative overflow-hidden aspect-[4/5] w-full max-w-[460px] ml-auto border border-[#D9B47A]/25 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)]"
          >
            <motion.img
              src="/images/back.jpg"
              alt="A woman's bare back emerging from shadow, draped in oxblood silk"
              style={{ y: imgY, scale: 1.15 }}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <motion.div className="absolute inset-0" style={{ background: spotBg }} />
          </motion.div>
          <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#A8998C] mt-5 text-right">
            Book I — A Path to Orgasmic Enlightenment
          </p>
        </motion.div>
      </div>
    </section>
  );
}
